/**
 * Services Section Component
 * Displays service offerings and the "How It Works" process
 */

export interface ServiceItem {
  title: string
  content: string
  button: string
}

export interface HowItWorksConfig {
  title: string
  subtitle: string
  steps: string[]
  currentClients: {
    subtitle: string
    steps: string[]
  }
  closing: string
  imageSrc: string
  currentClientsImageSrc: string
}

export interface ServicesConfig {
  title: string
  items: ServiceItem[]
  howItWorks: HowItWorksConfig
  onUploadClick?: () => void
  onPaymentClick?: () => void
  onPolicyClick?: () => void
}

export class Services {
  private config: ServicesConfig
  public element: HTMLElement

  constructor(config: ServicesConfig) {
    this.config = config
    this.element = this.render()
  }

  /**
   * Render the services section
   */
  render(): HTMLElement {
    const section = document.createElement('section')
    section.id = 'services'
    section.className = 'services-section section'

    section.innerHTML = `
      <div class="container">
        <h2 class="services-title text-center" data-i18n="services.title">
          ${this.config.title}
        </h2>

        <!-- Service Cards -->
        <div class="services-grid">
          ${this.config.items.map((service, index) => `
            <div class="service-card" data-index="${index}">
              <div class="service-card-icon">
                ${this.getServiceIcon(index)}
              </div>
              <h3 class="service-card-title" data-i18n="services.items.${index}.title">
                ${service.title}
              </h3>
              <p class="service-card-content" data-i18n="services.items.${index}.content">
                ${service.content}
              </p>
              <button class="service-card-btn" data-service-index="${index}" data-i18n="services.items.${index}.button">
                ${service.button}
              </button>
            </div>
          `).join('')}
        </div>

        <!-- How It Works -->
        <div class="how-it-works">
          <h3 class="how-it-works-title" data-i18n="services.howItWorks.title">
            ${this.config.howItWorks.title}
          </h3>
          <p class="how-it-works-subtitle" data-i18n="services.howItWorks.subtitle">
            ${this.config.howItWorks.subtitle}
          </p>
          
          <div class="how-it-works-content">
            <div class="how-it-works-image">
              <img src="${this.config.howItWorks.imageSrc}" alt="${this.config.howItWorks.title}" loading="lazy">
            </div>
            
            <div class="how-it-works-steps">
              <ol class="steps-list">
                ${this.config.howItWorks.steps.map((step, index) => `
                  <li class="step-item" data-i18n="services.howItWorks.steps.${index}">
                    ${step}
                  </li>
                `).join('')}
              </ol>
            </div>
          </div>

          <!-- Current Clients Section -->
          <p class="how-it-works-subtitle current-clients-subtitle" data-i18n="services.howItWorks.currentClients.subtitle">
            ${this.config.howItWorks.currentClients.subtitle}
          </p>
          
          <div class="how-it-works-content current-clients-content">
            <div class="how-it-works-steps">
              <ol class="steps-list">
                ${this.config.howItWorks.currentClients.steps.map((step, index) => `
                  <li class="step-item" data-i18n="services.howItWorks.currentClients.steps.${index}">
                    ${step}
                  </li>
                `).join('')}
              </ol>
            </div>
            
            <div class="how-it-works-image">
              <img src="${this.config.howItWorks.currentClientsImageSrc}" alt="${this.config.howItWorks.currentClients.subtitle}" loading="lazy">
            </div>
          </div>

          <p class="how-it-works-closing" data-i18n="services.howItWorks.closing">
            ${this.config.howItWorks.closing}
          </p>
        </div>
      </div>
    `

    return section
  }

  /**
   * Get SVG icon for a service card
   */
  private getServiceIcon(index: number): string {
    const icons = [
      // Estudio de Crédito - document/search icon
      `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`,
      // Gestión de Pagos - credit card icon
      `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>`,
      // Respaldo Integral - shield icon
      `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>`
    ]
    return icons[index] || icons[0]
  }

  /**
   * Initialize the component after it's added to the DOM
   */
  init(): void {
    this.attachEventListeners()
  }

  /**
   * Attach event listeners to service buttons
   */
  private attachEventListeners(): void {
    const buttons = this.element.querySelectorAll('.service-card-btn')
    
    console.log('Attaching event listeners to', buttons.length, 'buttons')
    
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const serviceIndex = parseInt((button as HTMLElement).getAttribute('data-service-index') || '0')
        
        console.log('Button clicked, service index:', serviceIndex)
        
        switch (serviceIndex) {
          case 0:
            // Iniciar Estudio
            console.log('Calling onUploadClick')
            if (this.config.onUploadClick) {
              this.config.onUploadClick()
            }
            break
          case 1:
            // Central de Pagos
            console.log('Calling onPaymentClick')
            if (this.config.onPaymentClick) {
              this.config.onPaymentClick()
            }
            break
          case 2:
            // Ver Políticas (abrir modal de protección de datos)
            console.log('Calling onPolicyClick')
            if (this.config.onPolicyClick) {
              this.config.onPolicyClick()
            }
            break
        }
      })
    })
  }
}
